/**
 * Shopware utilities for safe type access and data manipulation
 *
 * This module provides utility functions for safely accessing Shopware entity data,
 * particularly custom fields which can be null or undefined in API responses.
 */

import type { Product } from '~/types/shopware'

/**
 * Interface defining the structure of custom fields available on Product entities
 */
export interface ProductCustomFields {
  custom_product_fields_subtitle?: string
  custom_product_fields_edition?: string
  custom_product_fields_edition_statement?: string
  custom_product_fields_binding?: string
  custom_product_fields_weight?: string
  custom_product_fields_page_count?: string
  custom_product_fields_dimensions?: string
  custom_product_fields_editor?: string
  custom_product_fields_publisher?: string
  custom_product_fields_update_link?: string
  custom_product_fields_other_text?: string
  custom_product_fields_new?: boolean
  custom_product_fields_onsale?: boolean
  custom_product_fields_soon?: boolean
  custom_product_fields_will_be_replaced?: boolean
  custom_product_fields_publishing_status?: string
  custom_product_fields_publication_date?: string
  custom_product_first_language_code?: string
  custom_product_fields_features?: string[]
  [key: string]: string | string[] | boolean | undefined
}

/**
 * Safely access product-level custom fields
 *
 * @param product - The Product entity from Shopware API
 * @returns Typed custom fields object or null if not available
 *
 * @example
 * ```typescript
 * const customFields = getProductCustomFields(product)
 * const subtitle = customFields?.custom_product_fields_subtitle || 'Default subtitle'
 * ```
 */
export function getProductCustomFields(product: Product | null | undefined): ProductCustomFields | null {
  if (!product?.customFields || product.customFields === null) {
    return null
  }
  return product.customFields as ProductCustomFields
}

/**
 * Safely access translated custom fields from product
 *
 * @param product - The Product entity from Shopware API
 * @returns Typed translated custom fields object or null if not available
 *
 * @example
 * ```typescript
 * const customFields = getProductTranslatedCustomFields(product)
 * const localizedSubtitle = customFields?.custom_product_fields_subtitle || ''
 * ```
 */
export function getProductTranslatedCustomFields(product: Product | null | undefined): ProductCustomFields | null {
  if (!product) return null

  const translated = product.translated as Record<string, unknown> | undefined
  if (!translated?.customFields || translated.customFields === null) {
    return null
  }
  return translated.customFields as ProductCustomFields
}

/**
 * Get the display name for a product, preferring translated name
 *
 * @param product - The Product entity from Shopware API
 * @returns Product display name or fallback
 */
export function getProductDisplayName(product: Product | null | undefined): string {
  if (!product) return ''
  return product.translated?.name || product.name || ''
}

/**
 * Get product media URL with fallback
 *
 * @param product - The Product entity from Shopware API
 * @returns Image URL or empty string
 */
export function getProductImageUrl(product: Product | null | undefined): string {
  return product?.cover?.media?.url || ''
}

/**
 * Get product media alt text with fallback
 *
 * @param product - The Product entity from Shopware API
 * @returns Alt text for accessibility
 */
export function getProductImageAlt(product: Product | null | undefined): string {
  const alt = product?.cover?.media?.alt
  if (alt) return alt

  const name = getProductDisplayName(product)
  return name ? `${name} product image` : 'Product image'
}

/**
 * Check if product has any of the label flags set
 *
 * @param product - The Product entity from Shopware API
 * @returns Object with boolean flags for each label type
 */
export function getProductLabels(product: Product | null | undefined) {
  const customFields = getProductTranslatedCustomFields(product)

  return {
    new: Boolean(customFields?.custom_product_fields_new),
    onSale: Boolean(customFields?.custom_product_fields_onsale),
    soon: Boolean(customFields?.custom_product_fields_soon),
    willBeReplaced: Boolean(customFields?.custom_product_fields_will_be_replaced),
  }
}

/**
 * Resolve the ONIX PublicationDate label inputs for a product.
 *
 * Per CARW-1627, the publication date is shown whenever the product carries a
 * PublicationDate, independent of PublishingStatus or whether the date is in
 * the past or future:
 *  - YYYYMMDD / YYYYMM → month + year ("Mai 2026"); `monthIndex` is set
 *  - YYYY (year only)  → year only ("2023"); `monthIndex` is null
 *
 * Returns the decomposed year/monthIndex so the caller can format the month
 * name with `Intl.DateTimeFormat` in the active locale, or `null` when no
 * valid PublicationDate is present (row omitted).
 */
export function getProductPublicationDate(
  product: Product | null | undefined,
): { year: number; monthIndex: number | null } | null {
  const customFields = getProductTranslatedCustomFields(product) ?? getProductCustomFields(product)

  const raw = customFields?.custom_product_fields_publication_date
  if (typeof raw !== 'string' || !/^\d{4}(\d{2}(\d{2})?)?$/.test(raw)) return null

  const year = Number(raw.slice(0, 4))
  if (raw.length === 4) return { year, monthIndex: null }

  const monthIndex = Number(raw.slice(4, 6)) - 1
  const day = raw.length === 8 ? Number(raw.slice(6, 8)) : 1
  const date = new Date(year, monthIndex, day)

  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== monthIndex ||
    date.getDate() !== day
  ) {
    return null
  }

  return { year, monthIndex }
}

/**
 * Get product features as a typed array
 *
 * @param product - The Product entity from Shopware API
 * @returns Array of feature strings
 */
export function getProductFeatures(product: Product | null | undefined): string[] {
  const customFields = getProductTranslatedCustomFields(product) ?? getProductCustomFields(product)
  const features = customFields?.custom_product_fields_features

  if (Array.isArray(features)) {
    return features
  }

  return []
}
